import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/AuthProvider';
import { canRead } from '@/lib/permissions';
import type { Inserts, Tables, Updates } from '@/types/database';

/**
 * SECURITY: every projections hook below targets a `projections_*` table
 * that's gated to super_admin only via the RLS policies in
 * `supabase/migrations/25_projections.sql`. The `enabled` gate here is a
 * UX layer — it stops the browser from firing a request that RLS would
 * reject anyway, which keeps the network tab and Supabase logs clean for
 * non-super users.
 */
function useProjectionsEnabled(): boolean {
  const { role, loading } = useAuth();
  return !loading && canRead(role, 'financials');
}

/** Default scenario used everywhere until we wire up named scenarios. */
export const DEFAULT_SCENARIO = 'base';

// ---------------------------------------------------------------------------
// projections_capital — large planned capital transactions.
// ---------------------------------------------------------------------------

export function useProjectionsCapital(scenario: string = DEFAULT_SCENARIO) {
  const enabled = useProjectionsEnabled();
  return useQuery({
    queryKey: ['projections-capital', scenario],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projections_capital')
        .select('*')
        .eq('scenario', scenario)
        .order('txn_date');
      if (error) throw error;
      return data as Tables<'projections_capital'>[];
    },
  });
}

export function useInsertProjectionCapital() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: Omit<Inserts<'projections_capital'>, 'organization_id'>) => {
      if (!profile) throw new Error('Not signed in');
      const row: Inserts<'projections_capital'> = {
        ...input,
        organization_id: profile.organization_id,
      };
      const { data, error } = await supabase
        .from('projections_capital')
        .insert(row)
        .select('*')
        .single();
      if (error) throw error;
      return data as Tables<'projections_capital'>;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projections-capital'] }),
  });
}

export function useUpdateProjectionCapital() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: { id: string } & Updates<'projections_capital'>) => {
      const { data, error } = await supabase
        .from('projections_capital')
        .update(patch)
        .eq('id', id)
        .select('*')
        .single();
      if (error) throw error;
      return data as Tables<'projections_capital'>;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projections-capital'] }),
  });
}

export function useDeleteProjectionCapital() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('projections_capital').delete().eq('id', id);
      if (error) throw error;
      return id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projections-capital'] }),
  });
}

// ---------------------------------------------------------------------------
// projections_payroll — per-employee model.
// ---------------------------------------------------------------------------

export function useProjectionsPayroll(scenario: string = DEFAULT_SCENARIO) {
  const enabled = useProjectionsEnabled();
  return useQuery({
    queryKey: ['projections-payroll', scenario],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projections_payroll')
        .select('*, profiles(id, full_name, email)')
        .eq('scenario', scenario)
        .order('start_date');
      if (error) throw error;
      return data as Array<
        Tables<'projections_payroll'> & {
          profiles: Pick<Tables<'profiles'>, 'id' | 'full_name' | 'email'> | null;
        }
      >;
    },
  });
}

export function useInsertProjectionPayroll() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: Omit<Inserts<'projections_payroll'>, 'organization_id'>) => {
      if (!profile) throw new Error('Not signed in');
      const row: Inserts<'projections_payroll'> = {
        ...input,
        organization_id: profile.organization_id,
      };
      const { data, error } = await supabase
        .from('projections_payroll')
        .insert(row)
        .select('*')
        .single();
      if (error) throw error;
      return data as Tables<'projections_payroll'>;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projections-payroll'] }),
  });
}

export function useUpdateProjectionPayroll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: { id: string } & Updates<'projections_payroll'>) => {
      const { data, error } = await supabase
        .from('projections_payroll')
        .update(patch)
        .eq('id', id)
        .select('*')
        .single();
      if (error) throw error;
      return data as Tables<'projections_payroll'>;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projections-payroll'] }),
  });
}

export function useDeleteProjectionPayroll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('projections_payroll').delete().eq('id', id);
      if (error) throw error;
      return id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projections-payroll'] }),
  });
}

// ---------------------------------------------------------------------------
// projections_payroll_salaries — salary-change history (raises / pay-cuts).
// ---------------------------------------------------------------------------

export function useProjectionsPayrollSalaries(scenario: string = DEFAULT_SCENARIO) {
  const enabled = useProjectionsEnabled();
  return useQuery({
    // Scenario is not a column here; it scopes the parent payroll rows. We
    // key on it anyway so a future named-scenario switch re-fetches cleanly.
    queryKey: ['projections-payroll-salaries', scenario],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projections_payroll_salaries')
        .select('*')
        .order('effective_date');
      if (error) throw error;
      return data as Tables<'projections_payroll_salaries'>[];
    },
  });
}

export function useInsertProjectionPayrollSalary() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: Omit<Inserts<'projections_payroll_salaries'>, 'organization_id'>) => {
      if (!profile) throw new Error('Not signed in');
      const row: Inserts<'projections_payroll_salaries'> = {
        ...input,
        organization_id: profile.organization_id,
      };
      const { data, error } = await supabase
        .from('projections_payroll_salaries')
        .upsert(row, { onConflict: 'payroll_id,effective_date' })
        .select('*')
        .single();
      if (error) throw error;
      return data as Tables<'projections_payroll_salaries'>;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projections-payroll-salaries'] }),
  });
}

export function useUpdateProjectionPayrollSalary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...patch
    }: { id: string } & Updates<'projections_payroll_salaries'>) => {
      const { data, error } = await supabase
        .from('projections_payroll_salaries')
        .update(patch)
        .eq('id', id)
        .select('*')
        .single();
      if (error) throw error;
      return data as Tables<'projections_payroll_salaries'>;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projections-payroll-salaries'] }),
  });
}

export function useDeleteProjectionPayrollSalary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('projections_payroll_salaries').delete().eq('id', id);
      if (error) throw error;
      return id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projections-payroll-salaries'] }),
  });
}

// ---------------------------------------------------------------------------
// Bulk P&L upsert — used by the payroll "Auto-fill to P&L" button to write a
// month of projected payroll cost into projections_pnl in one round-trip.
// ---------------------------------------------------------------------------

export function useBulkUpsertProjectionPnl() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      cells: Array<{ account_id: string; month: string; amount_cents: number }>;
      scenario?: string;
    }) => {
      if (!profile) throw new Error('Not signed in');
      if (input.cells.length === 0) return 0;
      const rows: Inserts<'projections_pnl'>[] = input.cells.map((c) => ({
        organization_id: profile.organization_id,
        account_id: c.account_id,
        month: c.month,
        amount_cents: c.amount_cents,
        scenario: input.scenario ?? DEFAULT_SCENARIO,
      }));
      const { error } = await supabase
        .from('projections_pnl')
        .upsert(rows, { onConflict: 'organization_id,account_id,month,scenario' });
      if (error) throw error;
      return rows.length;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projections-pnl'] }),
  });
}
